import { SiteInfo } from '../../views/CalendarMultiView'
import { GanttMilestone } from '../../types/gantt.types'
import { useModalA11y } from '../../hooks/useModalA11y'
import { Z_POPUP } from '../../constants/zIndex'
import { EmptyState, DatePicker, Input, Button } from '@indipia/ui-core'
import { MILESTONE_COLORS, MILESTONE_DEFAULT_COLOR, FREQUENT_MILESTONE_TYPES, DEFAULT_MILESTONE_TYPE_NAMES } from '../../constants/gantt.const'
import { MilestoneTypeCode } from '../../context/CommonCodesContext'
// 마일스톤 관리 팝업(직접 만든 오버레이 + useModalA11y)

export type GanttMilestoneModalProps = {
  showMilestonePop: boolean
  setShowMilestonePop: React.Dispatch<React.SetStateAction<boolean>>
  selectedSite: SiteInfo
  milestones: GanttMilestone[]
  editingMsId: number | null
  milestoneTypeOptions: MilestoneTypeCode[]
  mileEditForm: { milestoneType: string | null; milestoneName: string; milestoneDate: string; }
  setMileEditForm: React.Dispatch<React.SetStateAction<{ milestoneType: string | null; milestoneName: string; milestoneDate: string; }>>
  setEditingMsId: React.Dispatch<React.SetStateAction<number | null>>
  setMileFormError: React.Dispatch<React.SetStateAction<string>>
  mileEditing: boolean
  handleMileEditSave: (milestoneId: number) => Promise<void>
  handleMileEditOpen: (m: GanttMilestone) => void
  mileDeleting: number | null
  handleMileDelete: (milestoneId: number) => Promise<void>
  mileForm: { milestoneType: string | null; milestoneName: string; milestoneDate: string; }
  setMileForm: React.Dispatch<React.SetStateAction<{ milestoneType: string | null; milestoneName: string; milestoneDate: string; }>>
  mileFormError: string
  isMileInvalid: boolean
  mileSaving: boolean
  handleMileAdd: () => Promise<void>
}

export function GanttMilestoneModal({
  showMilestonePop, setShowMilestonePop, selectedSite, milestones, editingMsId, milestoneTypeOptions,
  mileEditForm, setMileEditForm, setEditingMsId, setMileFormError, mileEditing, handleMileEditSave,
  handleMileEditOpen, mileDeleting, handleMileDelete, mileForm, setMileForm, mileFormError, isMileInvalid,
  mileSaving, handleMileAdd,
}: GanttMilestoneModalProps) {
  // 팝업별 접근성(포커스트랩·Esc 닫기) — 상시 마운트된 채 상태 플래그로만 팝업을 토글하므로
  // 각 플래그를 open으로 그대로 넘겨 열릴 때마다 다시 걸리게 한다.
  const milestoneModal   = useModalA11y(showMilestonePop, () => setShowMilestonePop(false))
  return (
    <>
      {showMilestonePop && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'var(--alpha-black-500)', zIndex: Z_POPUP, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowMilestonePop(false) }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={milestoneModal.titleId}
            tabIndex={-1}
            ref={milestoneModal.boxRef}
            style={{ background: 'var(--shell-bg)', borderRadius: 24, width: 540, maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'var(--elevation-3)' }}
          >
            {/* 팝업 헤더 */}
            <div style={{ background: 'var(--shell-surface)', padding: '14px 20px', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <span style={{ color: 'var(--gt-primary)', fontSize: 16 }}>◆</span>
              <span id={milestoneModal.titleId} style={{ fontSize: 15, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1 }}>마일스톤 관리</span>
              {selectedSite && <span style={{ fontSize: 12, color: 'var(--shell-on-surface-2)' }}>{selectedSite.siteName}</span>}
              <button
                onClick={() => setShowMilestonePop(false)}
                aria-label="닫기"
                style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'var(--shell-border)', color: 'var(--shell-on-surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontFamily: 'inherit', flexShrink: 0, lineHeight: 1 }}
              >×</button>
            </div>

            {/* 팝업 본문 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
              {/* 마일스톤 목록 */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--gt-primary)' }}>◆</span>
                  마일스톤 목록
                  <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 500, color: 'var(--shell-hint)' }}>({milestones.length}개)</span>
                </div>
                {milestones.length === 0 ? (
                  <EmptyState title="등록된 마일스톤이 없습니다" description="아래에서 새 마일스톤을 추가하세요" />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[...milestones].sort((a, b) => a.milestoneDate.localeCompare(b.milestoneDate)).map(m => {
                      const color = MILESTONE_COLORS[m.milestoneType ?? ''] ?? MILESTONE_DEFAULT_COLOR
                      const mDate = new Date(m.milestoneDate); mDate.setHours(0, 0, 0, 0)
                      const tDate = new Date(); tDate.setHours(0, 0, 0, 0)
                      const past  = mDate < tDate
                      const diff  = Math.round((mDate.getTime() - tDate.getTime()) / 86400000)
                      const dday  = diff > 0 ? `D-${diff}` : diff === 0 ? 'D-Day' : `D+${Math.abs(diff)}`
                      const [y, mo, d] = m.milestoneDate.split('-')
                      const isEditing = editingMsId === m.milestoneId
                      return (
                        <div key={m.milestoneId} style={{ background: 'var(--shell-surface)', border: `var(--border-width-hairline) solid ${isEditing ? color : `color-mix(in srgb, ${color} 25%, transparent)`}`, borderRadius: 12, padding: '12px 14px', opacity: (!isEditing && past) ? 0.65 : 1 }}>
                          {isEditing ? (
                            <>
                              <div style={{ marginBottom: 8 }}>
                                <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 4 }}>유형 <span style={{ color: 'var(--shell-hint)', fontWeight: 400 }}>(자주 쓰는 유형)</span></label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                  {FREQUENT_MILESTONE_TYPES.map(code => {
                                    const opt = milestoneTypeOptions.find((o: { typeCode: string; typeName: string }) => o.typeCode === code) as { typeCode: string; typeName: string } | undefined
                                    const name = opt?.typeName ?? DEFAULT_MILESTONE_TYPE_NAMES[code] ?? code
                                    const active = mileEditForm.milestoneType === code
                                    const chipColor = MILESTONE_COLORS[code] ?? 'var(--shell-on-surface-2)'
                                    return (
                                      <button
                                        key={code}
                                        type="button"
                                        onClick={() => setMileEditForm(f => ({ ...f, milestoneType: code, milestoneName: name }))}
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: 4,
                                          padding: '4px 10px', borderRadius: 24, fontSize: 'var(--type-caption-size)', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                                          border: `var(--border-width-hairline) solid ${active ? chipColor : 'var(--shell-border)'}`,
                                          background: active ? `color-mix(in srgb, ${chipColor} 12%, var(--shell-surface))` : 'var(--shell-bg)',
                                          color: active ? chipColor : 'var(--shell-on-surface-2)',
                                        }}
                                      >
                                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: chipColor, flexShrink: 0 }} />
                                        {name}
                                      </button>
                                    )
                                  })}
                                </div>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                                <div>
                                  <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 3 }}>날짜 <span style={{ color: 'var(--gt-primary)' }}>*</span></label>
                                  <DatePicker value={mileEditForm.milestoneDate} onChange={v => setMileEditForm(f => ({ ...f, milestoneDate: v }))} size="sm" className="!max-w-none" />
                                </div>
                                <div>
                                  <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 3 }}>라벨</label>
                                  <Input type="text" value={mileEditForm.milestoneName} onChange={e => setMileEditForm(f => ({ ...f, milestoneName: e.target.value, milestoneType: null }))} placeholder="비우면 유형명 사용" size="sm" className="!max-w-none" />
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                <Button variant="secondary" size="sm" className="!h-auto !px-3 !py-[5px] text-[length:var(--type-caption-size)]" onClick={() => { setEditingMsId(null); setMileFormError('') }}>취소</Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  className="!h-auto !px-3.5 !py-[5px] text-[length:var(--type-caption-size)]"
                                  style={{ background: color, borderColor: color }}
                                  loading={mileEditing}
                                  onClick={() => handleMileEditSave(m.milestoneId)}
                                >{mileEditing ? '저장 중...' : '저장'}</Button>
                              </div>
                            </>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ color, fontSize: 18, flexShrink: 0 }}>◆</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--shell-on-surface)', marginBottom: 2 }}>{m.milestoneName}</div>
                                <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  <span>{y}년 {Number(mo)}월 {Number(d)}일</span>
                                  {m.milestoneDescription && <><span>·</span><span>{m.milestoneDescription}</span></>}
                                </div>
                              </div>
                              <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: past ? 'var(--shell-on-surface-2)' : color, background: past ? 'var(--shell-skeleton)' : `color-mix(in srgb, ${color} 15%, transparent)`, padding: '3px 9px', borderRadius: 24, flexShrink: 0 }}>{dday}</span>
                              <Button
                                variant="secondary"
                                size="sm"
                                className="!h-auto shrink-0 !px-2.5 !py-[3px] text-[length:var(--type-caption-size)]"
                                style={{ borderColor: `color-mix(in srgb, ${color} 50%, transparent)`, color }}
                                onClick={() => handleMileEditOpen(m)}
                              >수정</Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                className="!h-auto shrink-0 !px-2.5 !py-[3px] text-[length:var(--type-caption-size)]"
                                style={{ borderColor: 'color-mix(in srgb, var(--color-error) 25%, var(--shell-surface))', color: 'var(--status-danger-600)' }}
                                loading={mileDeleting === m.milestoneId}
                                onClick={() => handleMileDelete(m.milestoneId)}
                              >{mileDeleting === m.milestoneId ? '' : '삭제'}</Button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* 추가 폼 */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--gt-primary)' }}>◆</span>
                  새 마일스톤 추가
                </div>

                {/* 유형: 자주 쓰는 유형을 칩으로 노출 — 선택 시 라벨에 그대로 반영. 라벨을 직접 수정하면
                    "기타"로 취급되어 어떤 칩도 선택되지 않은 상태로 바뀐다. */}
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 6 }}>유형 <span style={{ color: 'var(--shell-hint)', fontWeight: 400 }}>(자주 쓰는 유형)</span></label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {FREQUENT_MILESTONE_TYPES.map(code => {
                      const opt = milestoneTypeOptions.find((o: { typeCode: string; typeName: string }) => o.typeCode === code) as { typeCode: string; typeName: string } | undefined
                      const name = opt?.typeName ?? DEFAULT_MILESTONE_TYPE_NAMES[code] ?? code
                      const active = mileForm.milestoneType === code
                      const color = MILESTONE_COLORS[code] ?? 'var(--shell-on-surface-2)'
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() => setMileForm(f => ({ ...f, milestoneType: code, milestoneName: name }))}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '6px 12px', borderRadius: 24, fontSize: 'var(--type-caption-size)', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                            border: `var(--border-width-hairline) solid ${active ? color : 'var(--shell-border)'}`,
                            background: active ? `color-mix(in srgb, ${color} 12%, var(--shell-surface))` : 'var(--shell-surface)',
                            color: active ? color : 'var(--shell-on-surface-2)',
                          }}
                        >
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
                          {name}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 4 }}>날짜 <span style={{ color: 'var(--gt-primary)' }}>*</span></label>
                  <DatePicker
                    value={mileForm.milestoneDate}
                    onChange={v => setMileForm(f => ({ ...f, milestoneDate: v }))}
                    className="!max-w-none"
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 4 }}>라벨 <span style={{ color: 'var(--shell-hint)' }}>(직접 입력하면 입력한 라벨 그대로 등록)</span></label>
                  <Input
                    type="text"
                    value={mileForm.milestoneName}
                    onChange={e => setMileForm(f => ({ ...f, milestoneName: e.target.value, milestoneType: null }))}
                    placeholder="예: 2차 세대 입주 예정"
                    className="!max-w-none"
                  />
                </div>
                {mileFormError && <div className="field error" style={{ marginBottom: 10, maxWidth: 'none' }} role="alert"><span className="help">{mileFormError}</span></div>}
                <Button
                  variant="primary"
                  size="sm"
                  className="w-full gap-1.5"
                  disabled={isMileInvalid}
                  loading={mileSaving}
                  onClick={handleMileAdd}
                >
                  <span style={{ fontSize: 13 }}>◆</span>
                  {mileSaving ? '저장 중...' : '마일스톤 추가'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
